import React from "react";
import styles from "./WorkingTickets.module.scss";
import AddNote from "../../components/AddNote";
import { DataService } from "../../store/DataService";
import {useNavigate} from 'react-router-dom'; 
import GenButton from "../../components/GenButton";
import GenConfirm from "../../components/GenConfirm";
import TicketDetail from "../../components/TicketDetail";
import CountdownTimer from "../../components/CountdownTimer";
import { RELATIONSHIPS, APPTREASONS, ACTIONADDNOTE, ACTIONREASONS, formatPhoneNumber, Date12,WORKFLOW_ACTION_WAITING,WORKFLOW_ACTION_CLOSE, BASE_URL } from "../../AppConstants";

const WorkingTickets = (props) => {
    const [tickets,setTickets] = React.useState([]);
    const [selectedticket,setSelectedTicket] = React.useState(null);
    const [nowork,setNoWork] = React.useState(false);
    const [timerstarted,setTimerStarted] = React.useState(false);
    const lastticketRef = React.useRef({practiceid:0,ticketid:0,priority:0});
    const [isTimerActive,setIsTimerActive] = React.useState(false);
    const [timerDuration,setTimerDuration] = React.useState(30);
    const inTimeout = React.useRef(false);
    const rethome = React.useRef(false);
    const onbreak = React.useRef(false);
    const isClosingTicket = React.useRef(false);
    const history = useNavigate();

    const [reasons, setReasons] = React.useState([]);
    const [showReasonModal, setShowReasonModal] = React.useState(false);
    const [selectedReasonId, setSelectedReasonId] = React.useState(0);
    const pendingAction = React.useRef('immediate');
    const pendingReasonId = React.useRef(0);

    const [hangupNotice, setHangupNotice] = React.useState(null);
    const hangupTimerRef = React.useRef(null);

    const triggerHangupNotice = (msg = "The caller has hung up.", durationMs = 3000, deferFetch = false) => {
        if (hangupTimerRef.current) {
            clearTimeout(hangupTimerRef.current);
        }
        setHangupNotice({ msg, deferFetch });
        hangupTimerRef.current = setTimeout(() => {
            closeHangupNotice(deferFetch);
        }, durationMs);
    };

    const closeHangupNotice = (shouldFetch = false) => {
        if (hangupTimerRef.current) {
            clearTimeout(hangupTimerRef.current);
        }
        const mustFetch = typeof shouldFetch === 'boolean' ? shouldFetch : (hangupNotice?.deferFetch || false);
        setHangupNotice(null);
        if (mustFetch) {
            getAvailTickets();
        }
    };

    const BACK_TO_QUEUE = false;    // Timeout will set ticket back into QUEUE state
    const SHOW_TICKET_SUMMARY_LINE = false; // Flag to suppress ticket summary row on main screen before modal auto-pops

    React.useEffect(() => {
        const handleUnload = () => {
            const url = `${BASE_URL}resumeworking?uaid=${props.globals.useraccountid}&gid=${props.globals.sessionguid}`;
            navigator.sendBeacon(url);
        };

        window.addEventListener("beforeunload", handleUnload);
        return () => {
            window.removeEventListener("beforeunload", handleUnload);
        };
    }, [props.globals.useraccountid, props.globals.sessionguid]);

    React.useEffect(()=> {
        if (!timerstarted) {
            const dataservice = new DataService({guid:props.globals.sessionguid});
            
            // Fetch unavailable reasons
            dataservice.getunavailreasons().then((rdata) => {
                if (rdata.REASONS) {
                    setReasons(rdata.REASONS);
                }
            });

            // Check if user is already unavailable persistently
            dataservice.getuserunavailstatus(props.globals.useraccountid).then((data) => {
                if (data.UNAVAILABLE) {
                    onbreak.current = true;
                    setNoWork(true);
                } else {
                    getAvailTickets();
                }
                setTimerStarted(true);
            });
        } else {
            if (tickets.length === 0) {
                const timer = setInterval(()=> {
                    if (rethome.current===true) {
                        history("/");
                    } else {
                        if (nowork && selectedticket === null) {
                            getAvailTickets();
                        }    
                    }
                },3500); // Poll every 3.5 seconds for new available tickets (balanced server load & low latency)
                return () => {
                    clearInterval(timer);

                };
            }
        }
    },[nowork,selectedticket,timerstarted])

    // Poll status of active ticket to detect if taken by another agent, completed, or closed (only when on list screen)
    const isListPolling = React.useRef(false);
    const activeListTicketId = (tickets.length > 0 && selectedticket === null) ? tickets[0].ticketid : null;

    React.useEffect(() => {
        if (!activeListTicketId) return;

        const activeTicket = tickets[0];
        const initialEventName = activeTicket?.eventname || "";
        const initialStatus = parseInt(activeTicket?.eventstatus || activeTicket?.workingstateid || 0, 10);
        const isQueueTicket = (initialEventName === 'QUEUE' || initialStatus === 2 || initialStatus === 0);
        const dataservice = new DataService({ guid: props.globals.sessionguid });

        const statusPollInterval = setInterval(() => {
            if (isClosingTicket.current || isListPolling.current) return;
            isListPolling.current = true;
            dataservice.readticketdetail(props.globals.useraccountid, activeListTicketId)
                .then((data) => {
                    if (isClosingTicket.current) return;
                    if (data && data.TICKET) {
                        const ticketStatus = data.TICKET.ticketstatus || data.TICKET.Status || data.TICKET.status;
                        const ticketStatusInt = parseInt(ticketStatus || 0, 10);
                        
                        // 12 is CLOSED
                        if (ticketStatusInt === 12) {
                            lastticketRef.current = { practiceid: 0, ticketid: 0, priority: 0 };
                            setTickets([]);
                            setSelectedTicket(null);
                            setIsTimerActive(false);
                            triggerHangupNotice(`Caller hung up on Ticket #${activeListTicketId}.`, 3000, true);
                        }
                        // If it was a QUEUE ticket waiting to be taken, and its status is no longer QUEUE (2):
                        else if (isQueueTicket && ticketStatusInt > 0 && ticketStatusInt !== 2) {
                            lastticketRef.current = { practiceid: 0, ticketid: 0, priority: 0 };
                            setTickets([]);
                            setSelectedTicket(null);
                            setIsTimerActive(false);
                            getAvailTickets();
                        }
                    }
                })
                .finally(() => {
                    isListPolling.current = false;
                });
        }, 6000); // Check every 6 seconds

        return () => {
            if (statusPollInterval) clearInterval(statusPollInterval);
        };
    }, [activeListTicketId, props.globals.useraccountid, props.globals.sessionguid]);

    // Poll status when TicketDetail modal is open (selectedticket !== null) to auto-close if ticket leaves RESERVED/INPROGRESS or changes user
    const isModalPolling = React.useRef(false);
    const activeModalTicketId = (selectedticket && selectedticket.ticketid) ? selectedticket.ticketid : null;

    React.useEffect(() => {
        if (!activeModalTicketId) return;

        const currentUserId = parseInt(props.globals.useraccountid || 0, 10);
        const dataservice = new DataService({ guid: props.globals.sessionguid });

        const modalStatusPoll = setInterval(() => {
            if (isClosingTicket.current || isModalPolling.current) return;
            isModalPolling.current = true;

            dataservice.readticketdetail(props.globals.useraccountid, activeModalTicketId)
                .then((data) => {
                    if (isClosingTicket.current) return;
                    if (data && data.TICKET) {
                        const statusVal = data.TICKET.ticketstatus || data.TICKET.Status || data.TICKET.status;
                        const statusInt = parseInt(statusVal || 0, 10);
                        const ticketUserVal = data.TICKET.useraccountid || data.TICKET.userid || data.TICKET.UserID || 0;
                        const ticketUserInt = parseInt(ticketUserVal || 0, 10);

                        // Keep modal open IF status is RESERVED (7), INPROGRESS (3), WRAPUP (14), or TRANSFER (6) for the current user
                        const isValidStatus = (
                            statusInt === 7 || statusInt === 3 || statusInt === 14 || statusInt === 6 ||
                            statusVal === "RESERVED" || statusVal === "INPROGRESS" || statusVal === "WRAPUP" || statusVal === "TRANSFER"
                        );
                        const isValidUser = (ticketUserInt === currentUserId || ticketUserInt === 0);

                        if (isValidStatus && isValidUser) {
                            setSelectedTicket(data.TICKET);
                        } else {
                            console.log(`[UI-GUARD] Ticket ${activeModalTicketId} status=${statusInt}, user=${ticketUserInt}. Closing modal for user ${currentUserId}.`);
                            isClosingTicket.current = true;

                            // Check if ticket was in RESERVED status before closing
                            const initialStatusVal = selectedticket?.ticketstatus || selectedticket?.Status || selectedticket?.status;
                            const initialStatusInt = parseInt(initialStatusVal || 0, 10);
                            const wasReserved = (
                                initialStatusInt === 7 || initialStatusVal === 'RESERVED' ||
                                selectedticket?.eventname === 'RESERVED' || selectedticket?.eventstatus === 7
                            );

                            // Check if ticket closed because caller disconnected (status 12 / CLOSED)
                            const isClosed = (
                                statusInt === 12 || 
                                statusVal === 'CLOSED' || 
                                data.TICKET.isclosed === 1 || 
                                data.TICKET.closed === 1 ||
                                data.TICKET.eventname === 'CLOSED'
                            );

                            setSelectedTicket(null);
                            setTickets([]);
                            setIsTimerActive(false);

                            if (wasReserved) {
                                if (isClosed) {
                                    triggerHangupNotice(`Caller hung up on Ticket #${activeModalTicketId}.`, 3000, false);
                                    getAvailTickets();
                                } else {
                                    // Missed call: 10-second timeout, defer getAvailTickets until modal closes
                                    triggerHangupNotice(`Call was missed on Ticket #${activeModalTicketId}.`, 10000, true);
                                }
                            } else {
                                getAvailTickets();
                            }
                        }
                    }
                })
                .finally(() => {
                    isModalPolling.current = false;
                });
        }, 6000); // Check every 6 seconds

        return () => {
            if (modalStatusPoll) clearInterval(modalStatusPoll);
        };
    }, [activeModalTicketId, props.globals.useraccountid, props.globals.sessionguid]);

    const getAvailTickets = () => {
        if (inTimeout.current) return;
        if (onbreak.current) return;
        const pid = lastticketRef.current.practiceid;
        const tid = lastticketRef.current.ticketid;
        const pri = lastticketRef.current.priority;
        const dataservice = new DataService({guid:props.globals.sessionguid});
        
        setNoWork(false);
        
        dataservice.getwork(props.globals.practiceid,props.globals.useraccountid,pid,tid,pri).then((data) => {
            isClosingTicket.current = false;
            if (data.ERROR) {
                if (data.ERROR.length) {
                    if (data.ACTION === "inuse") {
                        getAvailTickets();
                        return;
                    }

                    if (props.globals.useraccountid !== 0) {
                        alert(data.ERROR);
                    }
                    if (data.ACTION) {
                        if (data.ACTION.length) {
                            if (data.ACTION==="LOGIN") {
                                props.methods.SetUseraccountid(0);
                                history("/");
                            }
                            if (data.ACTION==="EXIT") {
                                history("/");
                            }
                        }
                    }
                    return;
                }
            }
            setTickets(data.TICKETS);
            setNoWork(true);
            if (data.TICKETS.length > 0) {
                const firstTk = data.TICKETS[0];
                const ticketid = firstTk.ticketid;
                const evname = firstTk.eventname || "";
                const eventstatus = parseInt(firstTk.eventstatus || 0, 10);
                const servicetypeid = parseInt(firstTk.servicetypeid || 5, 10);
                
                if (servicetypeid === 6 || servicetypeid === 2) {
                    // Type B Practice (Live Message Taking): route directly to TakeNewMessage screen
                    let pobj = { practiceid: firstTk.practiceid || 0, practiceaccount: {}, practice: { name: firstTk.pracname || "" } };
                    props.methods.SetObject(pobj);
                    history('/takemsg', { state: { ticket: firstTk, returnToWorkingTickets: true } });
                } else {
                    // Type A Practice (EMR Direct Entry): Auto-pop modal if ticket is in RESERVED (7), INPROGRESS (3), or WRAPUP (14)
                    const isAutoPopState = (
                        evname === 'RESERVED' || evname === 'INPROGRESS' || evname === 'WRAPUP' ||
                        eventstatus === 7 || eventstatus === 3 || eventstatus === 14
                    );
                    
                    if (isAutoPopState) {
                        // 1. Immediately pop modal with initial ticket data from getwork (instant screen pop)
                        lastticketRef.current.practiceid = firstTk.practiceid || 0;
                        lastticketRef.current.ticketid = firstTk.ticketid || 0;
                        lastticketRef.current.priority = firstTk.priority || firstTk.evpriority || 0;
                        setSelectedTicket(firstTk);

                        // 2. Asynchronously fetch full ticket details in background and update modal state
                        dataservice.readticketdetail(props.globals.useraccountid, ticketid).then((tdData) => {
                            if (tdData && tdData.TICKET) {
                                lastticketRef.current.practiceid = tdData.TICKET.practiceid || 0;
                                lastticketRef.current.ticketid = tdData.TICKET.ticketid || 0;
                                lastticketRef.current.priority = tdData.TICKET.priority || 0;
                                setSelectedTicket(tdData.TICKET);
                            }
                        });
                    }
                }
            }
        })
    }

    const setSelected = () => {
        if (isTimerActive) {
            setIsTimerActive(false);
        }
        const dataservice = new DataService({guid:props.globals.sessionguid});
        const tk = tickets[0];
        if (!tk) return;
        const ticketid = tk.ticketid;
        const evname = tk.eventname || "";
        if (evname==='QUEUE' || evname==='INPROGRESS' || evname==='RESERVED' || evname==='WRAPUP') {
            const priority = tk.evpriority || 0;
            const stateid = tk.workingstateid || 0;
            dataservice.gototicketstate(ticketid,stateid,priority,props.globals.useraccountid).then((data)=>{
                if (data.hasOwnProperty('ERROR')) {
                    if (data.ERROR.length > 0) {
                        if (data.ACTION !== "inuse" && data.ACTION !== "invalidstate") {
                            alert(data.ERROR);
                        }
                    }
                }
                let closed = data.ISCLOSED || 0;
                if (closed === 1) {
                    alert("Ticket was already handled or closed.");
                    getAvailTickets();
                    return;
                }
    
                if (data.hasOwnProperty('ACTION')) {
                    let action = data.ACTION;
                    if (action === 'exit') {
                        getAvailTickets();
                        return;
                    }
                    if (action === 'inuse') {
                        alert("Ticket was taken by another agent or timed out.");
                        getAvailTickets();
                        return;
                    }
                    if (action === 'invalidstate') {
                        dataservice.readticketdetail(props.globals.useraccountid, ticketid).then((detailData) => {
                            if (detailData && detailData.TICKET && detailData.TICKET.ticketstatus === 12) {
                                alert("This ticket has already been completed by the system (caller disconnected).");
                            } else {
                                alert("This ticket has already been processed or its status has changed.");
                            }
                            getAvailTickets();
                        });
                        return;
                    }
                } 
                        
                const servicetypeid = parseInt(tk.servicetypeid || 5, 10);
                if (servicetypeid === 6 || servicetypeid === 2) {
                    let pobj = { practiceid: tk.practiceid || 0, practiceaccount: {}, practice: { name: tk.pracname || "" } };
                    props.methods.SetObject(pobj);
                    history('/takemsg', { state: { ticket: tk, returnToWorkingTickets: true } });
                } else {
                    dataservice.readticketdetail(props.globals.useraccountid,ticketid).then((data)=> {
                        if (data.ERROR) {
                            if (data.ERROR.length > 0) {
                                alert(data.ERROR);
                                return;
                            }
                        }
                        lastticketRef.current.practiceid = data.TICKET.practiceid || 0;
                        lastticketRef.current.ticketid = data.TICKET.ticketid || 0;
                        lastticketRef.current.priority = data.TICKET.priority || 0;
                        
                        setSelectedTicket(data.TICKET);
                    });
                }
            })
    
        }

    }

    const handleTimerComplete = (takeTicket) => {
        if (takeTicket) {
            setSelected();
        }
        setIsTimerActive(false);
        if (!takeTicket) {
            setSelectedTicket(null);
            const tk = tickets[0];
            setTickets([]);
            const priority = tk.evpriority || 0;
            const stateid = tk.restartstateid || 0;
            const dataservice = new DataService({guid:props.globals.sessionguid});
            if (BACK_TO_QUEUE) {
                dataservice.gototicketstate(tk.ticketid,stateid,priority,props.globals.useraccountid).then((data)=>{
                    if (data.hasOwnProperty('ERROR')) {
                        if (data.ERROR.length > 0) {
                            alert(data.ERROR);
                            return;
                        }
                    }
                    inTimeout.current = true;
                    rethome.current = true;
                    setIsTimerActive(true);
                    /*
                    inTimeout.current = true;
                    alert("Ticket timer has expired. Press OK to get available tickets");
                    inTimeout.current = false;
                    getAvailTickets();
                    */
                });
            } else {
                inTimeout.current = true;
                rethome.current = true;
                setIsTimerActive(true);
            }

        }
    }

    const handleButtonClick = (e) => {
        pendingAction.current = 'deferred';
        if (reasons.length > 0) {
            setSelectedReasonId(reasons[0].reasonid);
        }
        setShowReasonModal(true);
    }

    const handleBreakClick = (e) => {
        const dataservice = new DataService({guid:props.globals.sessionguid});
        dataservice.resumeworking(props.globals.useraccountid).then((data) => {
            setSelectedTicket(null);
            setTickets([]);
            onbreak.current = false;
            getAvailTickets();
        });
    }

    const handleBecomeUnavailableClick = (e) => {
        pendingAction.current = 'immediate';
        if (reasons.length > 0) {
            setSelectedReasonId(reasons[0].reasonid);
        }
        setShowReasonModal(true);
    }

    const handleConfirmReason = () => {
        setShowReasonModal(false);
        const reasonIdToUse = selectedReasonId || (reasons[0] ? reasons[0].reasonid : 0);
        
        if (pendingAction.current === 'immediate') {
            const dataservice = new DataService({guid:props.globals.sessionguid});
            dataservice.setuserunavail(props.globals.useraccountid, reasonIdToUse).then((data) => {
                onbreak.current = true;
                setTickets([]);
                setNoWork(true);
            });
        } else {
            pendingReasonId.current = reasonIdToUse;
            onbreak.current = true;
            setSelected();
        }
    }

    const ShowAvailable = () => {
        if (onbreak.current) {
            return (
                <React.Fragment>
                    <div style={{marginTop:"180px",marginLeft:"280px"}}>
                        <GenButton 
                            onclick={handleBreakClick} 
                            id={0} 
                            buttontext={"Resume Working"}
                            bgcolor="white"
                            color="black"
                            width="320"
                            height="80"
                            fontsize="32px"
                            bgcolor="yellow"
                        />
                        
                    </div>
                </React.Fragment>
            )
        }

        if (tickets.length === 0 || !SHOW_TICKET_SUMMARY_LINE) {
            return (
                <React.Fragment>
                    <div className={styles.title}>Available Working Tickets</div>
                    <div style={{marginBottom: "20px"}}>No Available Tickets</div>
                    <GenButton 
                        onclick={handleBecomeUnavailableClick} 
                        id={0} 
                        buttontext={"Become Unavailable"}
                        color="black"
                        width="240"
                        height="40"
                        fontsize="18px"
                        bgcolor="yellow"
                    />
                </React.Fragment>
            )
        }
        let tk = tickets[0];
        return (
            <React.Fragment>
                <div className={styles.title}>Available Working Tickets</div>
                <div className={styles.tickethdr}>
                    <div style={{textAlign:"right",paddingRight:"16px"}}>Ticket #</div>
                    <div>Practice</div>
                    <div>Message Type</div>
                    <div>Patient Name</div>
                    <div>Callback Phone</div>
                    <div>Created Date</div>
                    </div>
                    <div className={styles.ticketrow} onClick={setSelected}>
                        <div style={{textAlign:"right",paddingRight:"16px"}}>{tk.ticketid}</div>
                        <div>{tk.pracname}</div>
                        <div>{tk.msgname}</div>
                        <div>{tk.firstname} {tk.lastname}</div>
                        <div>{tk.callbackphone}</div>
                        <div>{Date12(tk.createdate)}</div>
                    </div>
                        
                {isTimerActive && 
                <div style={{marginTop:"30px",marginLeft:"160px"}}>
                    <CountdownTimer initialSeconds={timerDuration} onComplete={handleTimerComplete} />
                    {/*
                    <div style={{marginTop:"-37px",marginLeft:"320px"}}>
                        <GenButton 
                            onclick={handleButtonClick} 
                            id={0} 
                            buttontext={"Unavailable after this Ticket"}
                            bgcolor="white"
                            color="black"
                            width="240"
                            height="40"
                            fontsize="18px"
                            bgcolor="yellow"
                        />
                    </div>
                    */}
                </div>
                }
                
            </React.Fragment>
        )
    }

    const onDetailClose = React.useCallback((ticketid, stateid, priority, unavailReasonId) => {
        isClosingTicket.current = true;
        const dataservice = new DataService({ guid: props.globals.sessionguid });
        
        if (unavailReasonId !== undefined && unavailReasonId !== null && unavailReasonId > 0) {
            onbreak.current = true;
            pendingReasonId.current = unavailReasonId;
        }

        const finalizeClose = () => {
            setTickets([]);
            if (onbreak.current) {
                dataservice.setuserunavail(props.globals.useraccountid, pendingReasonId.current).then(() => {
                    setSelectedTicket(null);
                    getAvailTickets();
                });
            } else {
                setSelectedTicket(null);
                getAvailTickets();
            }
        };
        
        if (stateid === 0 && selectedticket) stateid = selectedticket.eventstatus;
        if (selectedticket && stateid === selectedticket.workingstateid) {
            const tid = selectedticket.ticketid;
            const pri = selectedticket.evpriority || 0;
            const rstate = selectedticket.restartstateid || 0;
            dataservice.gototicketstate(tid, rstate, pri, props.globals.useraccountid).then((data) => {
                if (data.hasOwnProperty('ERROR')) {
                    if (data.ERROR.length > 0) {
                        alert(data.ERROR);
                        return;
                    }
                }
                let closed = data.ISCLOSED || 0;
                if (closed === 1) {
                    alert("Ticket is Closed");
                    finalizeClose();
                    return;
                }
    
                if (data.hasOwnProperty('ACTION')) {
                    let action = data.ACTION;
                    if (action === 'exit' || action === 'inuse') {
                        if (onbreak.current) {
                            dataservice.setuserunavail(props.globals.useraccountid, pendingReasonId.current).then(() => {
                                getAvailTickets();
                            });
                        } else {
                            getAvailTickets();
                        }
                        return;
                    }
                }
                finalizeClose();
            });
        } else {
            finalizeClose();
        }
    }, [props.globals, selectedticket]);

    if (rethome.current===true) {
        props.globals.sysusermsg = 'Timeout while Working on tickets';
        history("/");
        return null;
    }
    return (
        <React.Fragment>
            {hangupNotice && (
                <div style={{
                    position: 'fixed',
                    top: '24px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#1e293b',
                    color: '#ffffff',
                    padding: '14px 24px',
                    borderRadius: '8px',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '18px',
                    zIndex: 10001,
                    fontSize: '16px',
                    fontWeight: '600',
                    borderLeft: '5px solid #ef4444',
                    minWidth: '320px',
                    justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '20px' }}>📞</span>
                        <span>{typeof hangupNotice === 'object' ? hangupNotice?.msg : hangupNotice}</span>
                    </div>
                    <button 
                        onClick={closeHangupNotice}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#94a3b8',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            padding: '0 4px',
                            lineHeight: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                        onMouseOver={(e) => e.target.style.color = '#ffffff'}
                        onMouseOut={(e) => e.target.style.color = '#94a3b8'}
                        title="Close"
                    >
                        ✕
                    </button>
                </div>
            )}

            {selectedticket===null && <ShowAvailable />}
            {selectedticket !== null && (
                <TicketDetail globals={props.globals} methods={props.methods} ticket={selectedticket} doClose={onDetailClose} />
            )}

            {showReasonModal && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: '#fff',
                        padding: '24px',
                        borderRadius: '8px',
                        width: '400px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        color: '#000'
                    }}>
                        <h3 style={{marginTop: 0, marginBottom: '16px', fontSize: '20px'}}>Select Unavailable Reason</h3>
                        <div style={{marginBottom: '20px'}}>
                            <label style={{display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold'}}>Reason:</label>
                            <select 
                                value={selectedReasonId} 
                                onChange={(e) => setSelectedReasonId(parseInt(e.target.value))}
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    fontSize: '16px'
                                }}
                            >
                                {reasons.map((r) => (
                                    <option key={r.reasonid} value={r.reasonid}>{r.reason}</option>
                                ))}
                            </select>
                        </div>
                        <div style={{display: 'flex', justifyContent: 'flex-end', gap: '12px'}}>
                            <GenButton 
                                onclick={() => setShowReasonModal(false)}
                                id={0}
                                buttontext="Cancel"
                                bgcolor="white"
                                color="black"
                                width="100"
                                height="36"
                                fontsize="16px"
                            />
                            <GenButton 
                                onclick={handleConfirmReason}
                                id={0}
                                buttontext="Confirm"
                                bgcolor="yellow"
                                color="black"
                                width="100"
                                height="36"
                                fontsize="16px"
                            />
                        </div>
                    </div>
                </div>
            )}
        </React.Fragment>
    )

}

export default WorkingTickets;